// Pure model behind the Custom connectors section: the starting template, the
// form <-> API translation, and the validation that mirrors the backend's own.
//
// Everything here is deliberately free of React so the rules that decide
// whether a collector can be saved are testable on their own — the same split
// as modbus-device-model.js.
//
// The backend contract lives in docs/battex/10-custom-collectors.md; the
// numeric bounds below mirror core/scada/collectors/collector.go, so a bad
// value is a message next to the field instead of a 400 banner.

export const MIN_POLL_INTERVAL_MS = 5_000;
export const DEFAULT_POLL_INTERVAL_MS = 60_000;
export const MAX_POLL_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

export const MIN_TIMEOUT_MS = 1_000;
export const DEFAULT_TIMEOUT_MS = 30_000;
export const MAX_TIMEOUT_MS = 300_000;

export const RUN_HISTORY_LIMIT = 20;

// Both patterns mirror the backend's: an output key is an identifier because
// the script types it by hand and the UI uses it as a column heading.
const OUTPUT_KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;
const ENV_KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

export const OUTPUT_TYPES = [
  { value: "float64", label: "float64" },
  { value: "int64", label: "int64" },
  { value: "bool", label: "bool" },
  { value: "string", label: "string" },
];

// The template a new collector starts from — deploy/gcp-pilot/collectors/
// example_cmg.py, header comment included. It is embedded rather than fetched
// so a new collector opens with a working script even on a gateway whose
// deploy/ tree was never shipped.
export const COLLECTOR_SCRIPT_TEMPLATE = `# example_cmg.py — the starting template for a Control Seat custom connector.
#
# A collector is a short script the gateway runs on a schedule. Everything it
# needs arrives on stdin as one JSON object, and its only job is to print one
# JSON object to stdout.
#
# stdin:
#   {
#     "params":   {...},    # the collector's non-secret params, verbatim
#     "now":      "2026-09-01T15:04:05Z",   # RFC 3339, UTC, when the pass began
#     "previous": {...}     # the values the last SUCCESSFUL pass returned, {} the first time
#   }
#
# stdout:
#   {"values": {"<key>": <number | string | boolean>, ...},
#    "meta":   {...}}      # optional, kept in the run history, never written to tags
#
# Rules that bite if you ignore them:
#   * Every key the collector's outputs table maps must appear in "values", or
#     the whole pass fails and NO tag is written. Extra keys are ignored, so it
#     is safe to return more than is mapped.
#   * Types are strict. An output declared int64 will not accept "42", and one
#     declared float64 will not accept a string. 3.0 for an int64 is fine —
#     JSON has one number type.
#   * Print diagnostics to STDERR, not stdout. The last non-blank stdout line is
#     parsed as a fallback, but stderr is where logs belong: the gateway keeps
#     the last 4 KB of it with every run, successful or not.
#   * Exit non-zero (or raise) to fail the pass deliberately. The traceback is
#     kept and shown next to the collector.
#   * The script runs as the driverhost's own user, with no site-packages and no
#     PYTHON* configuration (python -I). Only the standard library is
#     guaranteed. Secrets never live in the collector: name the environment
#     variable in env_keys and have the deployment allow it in
#     COLLECTORS_ENV_ALLOWLIST, then read it with os.environ.
#
# This template returns a fixed value so it works with no network. Replace
# fetch_cmg with a real request (urllib.request is in the standard library) and
# map "cmg_usd_mwh" to a tag path in the outputs table.

import json
import os
import sys


def fetch_cmg(params, previous):
    """Return the current marginal cost in USD/MWh.

    Replace this body with the real lookup. \`params\` is whatever the collector
    was configured with (a node name, a barra id, a URL); \`previous\` is the
    last successful values document, useful for computing a delta or for
    holding the last good reading when the upstream is briefly unavailable.
    """
    # A real implementation would look something like:
    #
    #   import urllib.request
    #   token = os.environ.get("CMG_API_TOKEN")   # listed in env_keys
    #   request = urllib.request.Request(
    #       params["url"], headers={"Authorization": f"Bearer {token}"})
    #   with urllib.request.urlopen(request, timeout=20) as response:
    #       payload = json.load(response)
    #   return float(payload["cmg"])
    del params, previous
    return 42.0


def main():
    request = json.load(sys.stdin)
    params = request.get("params") or {}
    previous = request.get("previous") or {}

    # Diagnostics go to stderr; the gateway keeps them with the run.
    print(f"collecting for barra={params.get('barra', 'demo')}", file=sys.stderr)

    values = {
        "cmg_usd_mwh": fetch_cmg(params, previous),
    }
    json.dump({"values": values, "meta": {"source": "example"}}, sys.stdout)
    sys.stdout.write("\\n")


if __name__ == "__main__":
    main()
`;

let outputCounter = 0;
const nextOutputUID = () => {
  outputCounter += 1;
  return `out-${outputCounter}`;
};

const trimmed = (value) => String(value ?? "").trim();

// ── Display ─────────────────────────────────────────────────────────

// "cada 60 s" / "cada 5 min" / "cada 2 h" — the poll rate as an operator says
// it out loud, not as milliseconds.
export function formatPollInterval(ms) {
  const seconds = Math.round(Number(ms) / 1000);
  if (!Number.isFinite(seconds) || seconds <= 0) return "sin intervalo";
  if (seconds < 120) return `cada ${seconds} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes % 60 === 0) return `cada ${minutes / 60} h`;
  return `cada ${minutes} min`;
}

export function formatRelativeTime(iso, now = Date.now()) {
  if (!iso) return "nunca";
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return "nunca";
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 60) return `hace ${seconds} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `hace ${hours} h`;
  return `hace ${Math.round(hours / 24)} d`;
}

export function formatDuration(ms) {
  const value = Number(ms);
  if (!Number.isFinite(value) || value < 0) return "—";
  if (value < 1000) return `${Math.round(value)} ms`;
  return `${(value / 1000).toFixed(1)} s`;
}

// Local wall clock, formatted by hand: the run list is scanned against the
// operator's own clock and toLocaleTimeString varies with the host locale.
export function formatClockTime(iso) {
  if (!iso) return "—";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(at.getHours())}:${pad(at.getMinutes())}:${pad(at.getSeconds())}`;
}

export function formatValue(value) {
  if (value === null || value === undefined) return "null";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

// Green when the last pass worked, red once a pass has failed, grey when the
// collector is not scheduled at all. "Scheduled" is the honest fourth state:
// enabled but not yet run, which is neither healthy nor broken.
export function collectorStatus(collector) {
  if (!collector?.enabled) return { key: "disabled", label: "Disabled" };
  if (Number(collector.consecutive_failures) > 0) {
    return { key: "failing", label: "Failing" };
  }
  if (!collector.last_run_at) return { key: "pending", label: "Scheduled" };
  return { key: "ok", label: "OK" };
}

// A returned value's JSON type decides the tag type when an output is added
// straight from a dry run. An integral number stays int64 only if the script
// clearly meant a count; a float is the safer default for anything else.
export function inferOutputType(value) {
  if (typeof value === "boolean") return "bool";
  if (typeof value === "number") return Number.isInteger(value) ? "int64" : "float64";
  if (typeof value === "string") return "string";
  return "string";
}

const slug = (text) =>
  trimmed(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export function suggestTagPath(collectorName, key) {
  const folder = slug(collectorName) || "collector";
  return `collectors/${folder}/${trimmed(key)}`;
}

// ── Params ──────────────────────────────────────────────────────────

// Empty is a legal "no params": the backend defaults an absent document to {}.
export function parseParams(text) {
  const raw = trimmed(text);
  if (!raw) return { ok: true, value: {} };
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    return { ok: false, error: error.message || "JSON inválido" };
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ok: false, error: "debe ser un objeto JSON, no una lista ni un valor suelto" };
  }
  return { ok: true, value: parsed };
}

// ── Form <-> record ─────────────────────────────────────────────────

const msToSecondsText = (ms, fallback) => {
  const value = Number(ms) > 0 ? Number(ms) : fallback;
  return String(value / 1000);
};

export function outputFormFromRecord(output = {}) {
  return {
    uid: nextOutputUID(),
    key: String(output.key || ""),
    tagPath: String(output.tag_path || ""),
    type: OUTPUT_TYPES.some((t) => t.value === output.type) ? output.type : "float64",
    historianEnabled: Boolean(output.historian_enabled),
    historianIntervalSeconds: String((Number(output.historian_interval_ms) || 0) / 1000),
    // A row the assistant proposed and the operator has not accepted yet. It
    // shows in the table marked as such, is never validated, and never travels
    // to the server.
    suggested: false,
  };
}

export function newOutputForm(patch = {}) {
  return { ...outputFormFromRecord({}), ...patch, uid: nextOutputUID() };
}

export function collectorFormFromRecord(record) {
  const params = record?.params;
  let paramsText = "{}";
  if (params !== undefined && params !== null) {
    try {
      paramsText = JSON.stringify(params, null, 2);
    } catch {
      paramsText = "{}";
    }
  }
  return {
    name: String(record?.name || ""),
    description: String(record?.description || ""),
    pollSeconds: msToSecondsText(record?.poll_interval_ms, DEFAULT_POLL_INTERVAL_MS),
    timeoutSeconds: msToSecondsText(record?.timeout_ms, DEFAULT_TIMEOUT_MS),
    params: paramsText,
    envKeys: Array.isArray(record?.env_keys) ? record.env_keys.map(String) : [],
    // A new collector opens on the template rather than an empty box: the
    // stdin/stdout contract is not guessable, and the header comment is the
    // shortest documentation an author will actually read.
    script: record?.script ? String(record.script) : COLLECTOR_SCRIPT_TEMPLATE,
    outputs: Array.isArray(record?.outputs) ? record.outputs.map(outputFormFromRecord) : [],
  };
}

// ── Assistant proposals ─────────────────────────────────────────────

// The outputs the AI proposes alongside a script land in the table as
// suggestions, not as configuration: the operator accepts each one before it
// counts. A key that is already mapped is left alone — the row may carry a tag
// path and a historian setting the assistant knows nothing about — and any
// still-pending suggestion is dropped first, so proposing twice replaces the
// last set instead of stacking duplicates.
export function applyOutputSuggestions(form, suggestions = []) {
  const kept = form.outputs.filter((output) => !output.suggested);
  const taken = new Set(kept.map((output) => trimmed(output.key)).filter(Boolean));
  const added = [];
  for (const suggestion of Array.isArray(suggestions) ? suggestions : []) {
    const key = trimmed(suggestion?.key);
    if (!key || taken.has(key)) continue;
    taken.add(key);
    added.push(
      newOutputForm({
        key,
        type: OUTPUT_TYPES.some((t) => t.value === suggestion?.type) ? suggestion.type : "float64",
        tagPath: trimmed(suggestion?.tag_path) || suggestTagPath(form.name, key),
        suggested: true,
      })
    );
  }
  return { ...form, outputs: [...kept, ...added] };
}

// ── Payload ─────────────────────────────────────────────────────────

const secondsToMs = (text) => Math.round(Number(text) * 1000);

export function buildCollectorPayload(form, { create = false } = {}) {
  const payload = {
    name: trimmed(form.name),
    description: trimmed(form.description),
    script: form.script,
    poll_interval_ms: secondsToMs(form.pollSeconds),
    timeout_ms: secondsToMs(form.timeoutSeconds),
    params: parseParams(form.params).value,
    env_keys: form.envKeys.map(trimmed).filter(Boolean),
    // A suggestion the operator has not accepted is not configuration yet.
    outputs: form.outputs.filter((output) => !output.suggested).map((output) => ({
      key: trimmed(output.key),
      tag_path: trimmed(output.tagPath),
      type: output.type,
      historian_enabled: Boolean(output.historianEnabled),
      // The interval only means anything with the historian on, and the
      // backend rejects a negative one, so a disabled row always sends 0.
      historian_interval_ms: output.historianEnabled
        ? Math.max(0, secondsToMs(output.historianIntervalSeconds || 0) || 0)
        : 0,
    })),
  };
  if (create) {
    payload.language = "python";
    // A brand-new collector never starts running: the operator enables it from
    // the list once a dry run looks right.
    payload.enabled = false;
  }
  return payload;
}

// ── Validation ──────────────────────────────────────────────────────

export function validateCollectorForm(form) {
  const errors = {};

  if (!trimmed(form.name)) errors.name = "Ponle un nombre al colector.";

  const pollMs = secondsToMs(form.pollSeconds);
  if (!Number.isFinite(pollMs) || pollMs < MIN_POLL_INTERVAL_MS) {
    errors.pollSeconds = `El intervalo mínimo es de ${MIN_POLL_INTERVAL_MS / 1000} segundos.`;
  } else if (pollMs > MAX_POLL_INTERVAL_MS) {
    errors.pollSeconds = "El intervalo máximo es de 7 días.";
  }

  const timeoutMs = secondsToMs(form.timeoutSeconds);
  if (!Number.isFinite(timeoutMs) || timeoutMs < MIN_TIMEOUT_MS || timeoutMs > MAX_TIMEOUT_MS) {
    errors.timeoutSeconds = `El timeout debe estar entre ${MIN_TIMEOUT_MS / 1000} y ${
      MAX_TIMEOUT_MS / 1000
    } segundos.`;
  }

  const params = parseParams(form.params);
  if (!params.ok) errors.params = `Los params no son válidos: ${params.error}`;

  if (!trimmed(form.script)) errors.script = "El script no puede estar vacío.";

  const badEnvKey = form.envKeys.find((key) => !ENV_KEY_PATTERN.test(trimmed(key)));
  if (badEnvKey !== undefined) {
    errors.envKeys = `"${badEnvKey}" no es un nombre de variable de entorno válido.`;
  }

  const outputs = {};
  const keySeen = new Map();
  const pathSeen = new Map();
  // Pending suggestions are skipped: they are not saved, so complaining about
  // one would block a Save the operator never asked to include it in.
  form.outputs.filter((output) => !output.suggested).forEach((output) => {
    const key = trimmed(output.key);
    const tagPath = trimmed(output.tagPath);
    const rowErrors = {};
    if (!key) {
      rowErrors.key = "Falta la key.";
    } else if (!OUTPUT_KEY_PATTERN.test(key)) {
      rowErrors.key = "La key debe empezar con letra o guion bajo y seguir con letras, dígitos o guiones bajos.";
    } else if (keySeen.has(key)) {
      rowErrors.key = "Esta key está mapeada dos veces.";
      outputs[keySeen.get(key)] = {
        ...outputs[keySeen.get(key)],
        key: "Esta key está mapeada dos veces.",
      };
    } else {
      keySeen.set(key, output.uid);
    }
    if (!tagPath) {
      rowErrors.tagPath = "Falta el tag path.";
    } else if (pathSeen.has(tagPath)) {
      rowErrors.tagPath = "Dos outputs escriben en este mismo tag.";
      outputs[pathSeen.get(tagPath)] = {
        ...outputs[pathSeen.get(tagPath)],
        tagPath: "Dos outputs escriben en este mismo tag.",
      };
    } else {
      pathSeen.set(tagPath, output.uid);
    }
    if (Object.keys(rowErrors).length > 0) {
      outputs[output.uid] = { ...outputs[output.uid], ...rowErrors };
    }
  });
  if (Object.keys(outputs).length > 0) errors.outputs = outputs;

  return errors;
}

// The server answers with plain text ("collectors: invalid input: timeout_ms
// must be between 1000 and 300000"). Naming the field it is about puts the
// message under the input the operator has to fix instead of in a banner.
export function serverErrorField(message) {
  const text = String(message || "").toLowerCase();
  if (text.includes("poll_interval_ms")) return "pollSeconds";
  if (text.includes("timeout_ms")) return "timeoutSeconds";
  if (text.includes("params")) return "params";
  if (text.includes("script")) return "script";
  if (text.includes("env key")) return "envKeys";
  if (text.includes("output") || text.includes("tag_path")) return "outputs";
  if (text.includes("name")) return "name";
  return "";
}
